import { useState, useEffect, useCallback } from 'react';
import { localVendorsAPI, storesAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Search, RefreshCw, Truck, Store, IndianRupee, 
  Eye, TrendingUp, Users, Building2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const LocalVendorMonitoring = () => {
  const { toast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeSummary, setStoreSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [vendorType, setVendorType] = useState('all');
  const [search, setSearch] = useState('');
  
  // Store detail dialog
  const [storeDetailOpen, setStoreDetailOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storePurchases, setStorePurchases] = useState([]);
  const [loadingStoreDetail, setLoadingStoreDetail] = useState(false);

  const fetchStores = useCallback(async () => {
    try {
      const response = await storesAPI.list({ page_size: 200 });
      const franchiseStores = (response.data.items || []).filter(s => s.store_type === 'franchise');
      setStores(franchiseStores);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (selectedStoreId && selectedStoreId !== 'all') params.store_id = selectedStoreId;
      if (vendorType && vendorType !== 'all') params.vendor_type = vendorType;
      if (search) params.search = search;
      
      const response = await localVendorsAPI.hqListAll(params);
      const data = response.data;
      setVendors(data.items || []);
      setStoreSummary(data.store_summary || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast({ title: 'Error', description: 'Failed to fetch local vendors', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedStoreId, vendorType, search, toast]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const viewStoreDetail = async (storeId) => {
    const store = stores.find(s => s.id === storeId) || storeSummary.find(s => s._id === storeId);
    setSelectedStore(store);
    setStoreDetailOpen(true);
    setLoadingStoreDetail(true);
    
    try {
      const response = await localVendorsAPI.hqGetStorePurchases(storeId, { page_size: 50 });
      setStorePurchases(response.data);
    } catch (error) {
      console.error('Error fetching store purchases:', error);
      toast({ title: 'Error', description: 'Failed to fetch store details', variant: 'destructive' });
    } finally {
      setLoadingStoreDetail(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  // Calculate totals
  const totalVendors = vendors.length;
  const totalBalance = vendors.reduce((sum, v) => sum + (v.current_balance || 0), 0);
  const totalPurchases = vendors.reduce((sum, v) => sum + (v.total_purchases || 0), 0);

  return (
    <div className="p-6 space-y-6" data-testid="local-vendor-monitoring-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Local Vendor Monitoring
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor franchise store local vendors and their purchases
          </p>
        </div>
        <Button onClick={fetchVendors} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Vendors</p>
                <p className="text-2xl font-bold text-blue-600">{totalVendors}</p>
              </div>
              <Users className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Stores with Vendors</p>
                <p className="text-2xl font-bold text-purple-600">{storeSummary.length}</p>
              </div>
              <Store className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(totalBalance)}</p>
              </div>
              <IndianRupee className="w-8 h-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Purchases</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalPurchases)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store Summary */}
      {storeSummary.length > 0 && (
        <Card>
          <CardHeader className="py-3 bg-gray-50/50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gray-500" />
              By Store
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {storeSummary.map((store) => (
                <Card 
                  key={store._id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => viewStoreDetail(store._id)}
                >
                  <CardContent className="p-4">
                    <p className="font-medium text-gray-900 truncate">{store.store_name}</p>
                    <p className="text-xs text-gray-500">{store.store_code}</p>
                    <div className="mt-2 flex justify-between text-sm">
                      <span className="text-gray-500">{store.vendor_count} vendors</span>
                      <span className="text-orange-600 font-medium">{formatCurrency(store.total_balance)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search vendors..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-[200px]">
              <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                <SelectTrigger>
                  <SelectValue placeholder="All Stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name} ({store.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[150px]">
              <Select value={vendorType} onValueChange={setVendorType}>
                <SelectTrigger>
                  <SelectValue placeholder="Vendor Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="franchise">Franchise Managed</SelectItem>
                  <SelectItem value="hq_assigned">HQ Assigned</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vendors List */}
      <Card>
        <CardHeader className="py-3 bg-gray-50/50 border-b">
          <CardTitle className="text-lg">All Local Vendors ({vendors.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Vendor</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead className="text-center">Type</TableHead>
                <TableHead className="text-right">Purchases</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Loading vendors...
                  </TableCell>
                </TableRow>
              ) : vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No local vendors found
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map((vendor) => (
                  <TableRow key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{vendor.name}</p>
                        <p className="text-xs text-gray-500">{vendor.code}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{vendor.store_name}</p>
                        <p className="text-xs text-gray-500">{vendor.store_code}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{vendor.phone}</TableCell>
                    <TableCell className="text-sm text-gray-600">{vendor.gstin || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={vendor.vendor_type === 'franchise' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'}>
                        {vendor.vendor_type === 'franchise' ? 'Franchise' : 'HQ Assigned'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(vendor.total_purchases)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={vendor.current_balance > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                        {formatCurrency(vendor.current_balance)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Store Detail Dialog */}
      <Dialog open={storeDetailOpen} onOpenChange={setStoreDetailOpen}>
        <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-blue-600" />
              {selectedStore?.store_name || selectedStore?.name || 'Store'} - Local Purchases
            </DialogTitle>
          </DialogHeader>
          
          {loadingStoreDetail ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="space-y-4">
              {/* Summary */}
              {storePurchases.summary && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600">Vendors</p>
                    <p className="text-lg font-bold text-blue-700">{storePurchases.summary.vendor_count}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-600">Total GRNs</p>
                    <p className="text-lg font-bold text-green-700">{storePurchases.summary.total_grns}</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-xs text-purple-600">Purchase Value</p>
                    <p className="text-lg font-bold text-purple-700">{formatCurrency(storePurchases.summary.total_amount)}</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-xs text-orange-600">Outstanding</p>
                    <p className="text-lg font-bold text-orange-700">{formatCurrency(storePurchases.summary.total_vendor_balance)}</p>
                  </div>
                </div>
              )}

              {/* Vendors */}
              {storePurchases.vendors && storePurchases.vendors.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Local Vendors</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>GSTIN</TableHead>
                        <TableHead className="text-right">Purchases</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {storePurchases.vendors.map(vendor => (
                        <TableRow key={vendor.id}>
                          <TableCell>
                            <p className="font-medium">{vendor.name}</p>
                            <p className="text-xs text-gray-500">{vendor.code}</p>
                          </TableCell>
                          <TableCell>{vendor.phone}</TableCell>
                          <TableCell>{vendor.gstin || '-'}</TableCell>
                          <TableCell className="text-right">{formatCurrency(vendor.total_purchases)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatCurrency(vendor.current_balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Recent Purchases */}
              {storePurchases.purchases && storePurchases.purchases.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Recent Local Purchases</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>GRN #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor Invoice</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {storePurchases.purchases.map(grn => (
                        <TableRow key={grn.id}>
                          <TableCell className="font-mono text-sm">{grn.grn_number}</TableCell>
                          <TableCell>{formatDate(grn.created_at)}</TableCell>
                          <TableCell>{grn.vendor_invoice_number || '-'}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(grn.grand_total)}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{grn.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocalVendorMonitoring;
